import { useEffect, useRef, useState } from "react";

import { useInView } from "../hooks";
import { formatMxn, MOCK_PRICES } from "../mockData";
import { publicAsset } from "../publicAsset";
import { MockFigure } from "./frames";
import "./MoneyFlow.css";

/** Comisión de Torneos123 por jugador inscrito (ver PRECIO_TEXTO). */
const COMISION_TORNEOS123 = 99;
/** Inscripción General de la Copa Otoño 2026 (universo de ejemplo). */
const PAGO_JUGADOR = MOCK_PRICES[0]?.amount ?? 850;
const A_TU_CUENTA = PAGO_JUGADOR - Math.min(COMISION_TORNEOS123, PAGO_JUGADOR);

const FLOW_LABEL =
  `Ejemplo de cómo se reparte un pago en línea: el jugador paga ${formatMxn(PAGO_JUGADOR)}; ` +
  `Mercado Pago separa en automático ${formatMxn(COMISION_TORNEOS123)} para Torneos123 y deposita ` +
  `${formatMxn(A_TU_CUENTA)} en tu cuenta, menos su propia comisión.`;

function ArrowGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h13" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

function PhoneGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="2.5" width="12" height="19" rx="2.6" />
      <path d="M10.5 18.2h3" />
      <path d="M9.2 9.6h5.6M9.2 12.6h3.4" />
    </svg>
  );
}

function SplitGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h6" />
      <path d="M9 12c3.2 0 3.8-5.5 7.4-5.5H21" />
      <path d="M9 12c3.2 0 3.8 5.5 7.4 5.5H21" />
      <path d="m18.2 3.8 2.8 2.7-2.8 2.7" />
      <path d="m18.2 14.8 2.8 2.7-2.8 2.7" />
    </svg>
  );
}

function WalletGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18v3" />
      <path d="M4 7.5v10A2.5 2.5 0 0 0 6.5 20H20v-4" />
      <path d="M20 8H6.5A2.5 2.5 0 0 1 4 7.5" />
      <path d="M20 12h-3.5a2 2 0 0 0 0 4H20v-4Z" />
    </svg>
  );
}

/**
 * M3 · Flujo del dinero: el jugador paga → Mercado Pago separa → tu cuenta y
 * Torneos123. Diagrama claro (tarjetas blancas, acentos verdes y flechas lima
 * sobre azul marino). Es horizontal cuando cabe y vertical en espacios
 * angostos: decide por el ancho de su propio contenedor, así funciona igual
 * en la escena de cobro que dentro del recuadro «¿Quién paga los $99?».
 * `compact` quita el título y el marco de tarjeta para ir dentro de otra
 * tarjeta.
 */
export function MoneyFlow({ compact = false }: { compact?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, "0px 0px -15% 0px");
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    if (inView) setPlayed(true);
  }, [inView]);

  const rootClass = ["mk-money", compact ? "mk-money--compact" : "", played ? "is-flowing" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <MockFigure label={FLOW_LABEL} className="mk-money-fig">
      <div ref={ref} className={rootClass}>
        {compact ? null : <p className="mk-money__title">Así se reparte cada pago en línea</p>}
        <div className="mk-money__body">
          <div className="mk-money__flow">
            <div className="mk-money__node mk-money__node--player">
              <span className="mk-money__icon">
                <PhoneGlyph />
              </span>
              <span className="mk-money__label">El jugador paga</span>
              <strong className="mk-money__amount">{formatMxn(PAGO_JUGADOR)}</strong>
              <span className="mk-money__note">El costo que tú fijas</span>
            </div>

            <div className="mk-money__conn">
              <span className="mk-money__pulse">
                <i />
              </span>
              <span className="mk-money__chip">
                <ArrowGlyph />
              </span>
            </div>

            <div className="mk-money__node mk-money__node--split">
              <span className="mk-money__icon">
                <SplitGlyph />
              </span>
              <strong className="mk-money__name">Mercado Pago</strong>
              <span className="mk-money__note">separa en automático</span>
            </div>

            <div className="mk-money__fork">
              <span className="mk-money__branch mk-money__branch--you" />
              <span className="mk-money__branch mk-money__branch--t123" />
            </div>

            <div className="mk-money__outs">
              <div className="mk-money__out mk-money__out--you">
                <span className="mk-money__out-head">
                  <span className="mk-money__icon mk-money__icon--sm">
                    <WalletGlyph />
                  </span>
                  Tu cuenta
                </span>
                <strong className="mk-money__amount">{formatMxn(A_TU_CUENTA)}</strong>
                <span className="mk-money__note">menos la comisión de Mercado Pago</span>
              </div>
              <div className="mk-money__out mk-money__out--t123">
                <span className="mk-money__out-head">
                  <img
                    className="mk-money__logo"
                    src={publicAsset("torneos123/icon-torneos123-192.png")}
                    alt=""
                    width={192}
                    height={192}
                    loading="lazy"
                    decoding="async"
                  />
                  Torneos123
                </span>
                <strong className="mk-money__amount">{formatMxn(COMISION_TORNEOS123)}</strong>
                <span className="mk-money__note">por jugador inscrito</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MockFigure>
  );
}
